<script lang="ts">
import DataSourceToggle from "@/components/datascience/DataSourceToggle.vue";
import type { TreeAccordionNode } from "@/components/ui/TreeAccordion.vue";
import { RouteNames } from "@/router";
import { getColorInGradient, intersect, remap } from "@/services/utils";

interface PlotDimesion {
  range: [number, number];
  label: string;
  name: string;
  values: number[];
  position: number;
  greater_is_better: boolean | null;
}

/**
 * Map experiment groups to a tree
 */
export function experimentGroupsToTree(groups: EstimatorReportGroupDto[]): TreeAccordionNode[] {
  const tree: TreeAccordionNode[] = [];

  // Group by dataset
  const datasetGroups = new Map<string, Array<{ ml_task: string; estimator_class_name: string }>>();
  groups.forEach((group) => {
    const dataset = group.dataset;
    if (!datasetGroups.has(dataset)) {
      datasetGroups.set(dataset, []);
    }
    datasetGroups.get(dataset)?.push(...group.estimator_reports);
  });

  // Create tree structure
  for (const [dataset, reports] of datasetGroups) {
    // Group by ml_task
    const taskGroups = new Map<string, Array<{ estimator_class_name: string }>>();
    reports.forEach((report) => {
      if (!taskGroups.has(report.ml_task)) {
        taskGroups.set(report.ml_task, []);
      }
      taskGroups.get(report.ml_task)?.push(report);
    });

    // Create dataset node
    const datasetNode: TreeAccordionNode = {
      name: dataset,
      label: `Dataset #${dataset.slice(0, 6)}`,
      enabled: true,
      icon: "icon-database",
      foldable: true,
      children: [],
    };

    // Add ml_task nodes
    for (const [task, taskReports] of taskGroups) {
      // Group by estimator
      const estimatorCounts = new Map<string, number>();
      taskReports.forEach((report) => {
        estimatorCounts.set(
          report.estimator_class_name,
          (estimatorCounts.get(report.estimator_class_name) || 0) + 1
        );
      });

      const taskNode: TreeAccordionNode = {
        name: task,
        enabled: true,
        foldable: true,
        icon: "icon-sphere",
        totalCount: taskReports.length,
        children: Array.from(estimatorCounts).map(([estimator, count]) => ({
          name: estimator,
          enabled: true,
          icon: "icon-cube",
          totalCount: count,
        })),
      };

      datasetNode.children?.push(taskNode);
    }

    tree.push(datasetNode);
  }

  return tree;
}
</script>

<script setup lang="ts">
import { computed, onMounted, ref, shallowRef, unref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import PlotlyWidget from "@/components/datascience/PlotlyWidget.vue";
import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import TreeAccordion from "@/components/ui/TreeAccordion.vue";
import { type EstimatorReportGroupDto, type EstimatorReportSearchResultDto } from "@/dto";
import GridLayoutWithSidebar from "@/layouts/GridLayoutWithSidebar.vue";
import { useApi } from "@/services/api";
import ProjectDetailViewExperimentsSearchResult from "@/views/projects/ProjectDetailViewExperimentsSearchResult.vue";

import gradient from "@/assets/images/copper_gradient.png";

const api = useApi();
const route = useRoute();
const router = useRouter();
const isLoadingGroups = ref(true);
const isLoadingPlot = ref(true);
const isLoadingSearch = ref(true);
const estimatorReportsGroups = shallowRef<EstimatorReportGroupDto[]>([]);
const plotReports = shallowRef<EstimatorReportSearchResultDto[]>([]);
const searchReports = shallowRef<EstimatorReportSearchResultDto[]>([]);
const searchReportsColors = ref<string[]>([]);
const selectedGroup = ref<string[]>([]);
const dimensions = ref<PlotDimesion[]>([]);
const colorByDimensionIndex = ref(-1);
const isTestDatasource = ref(true);
let searchParams = new URLSearchParams();
const dimensionsNameByLabel = new Map<string, string>();
const gradientStops = await Promise.all(
  Array.from({ length: 10 }, (_, i) => i / 9).map(async (i) => [
    i,
    await getColorInGradient(i, gradient),
  ])
);

const tenantName = computed(() => route.params.tenantName as string);
const projectName = computed(() => route.params.projectName as string);
const groupsAsTree = computed(() => {
  const groups = unref(estimatorReportsGroups);
  return experimentGroupsToTree(groups);
});
const plotData = computed(() => {
  const margin = 15;
  const fontSize = 14;
  const longestDimensionNameLength = Math.max(...dimensions.value.map((d) => d.label.length));
  const data = {
    data: [
      {
        type: "parcoords",
        line: {
          color: [1],
          colorbar: { title: { text: "" }, tickfont: { size: 8 } },
          colorscale: gradientStops,
          showscale: true,
          reversescale: false,
          width: 3,
        },
        dimensions: dimensions.value,
      },
    ],
    layout: {
      margin: {
        t: fontSize * 2 + margin,
        l: longestDimensionNameLength * fontSize * 0.6 * 0.5,
        b: fontSize + margin,
        r: 0,
      },
      font: {
        family: "GeistMono, monospace",
        size: fontSize,
      },
    },
    frames: [],
  };
  const colorDimension = dimensions.value[colorByDimensionIndex.value];
  if (colorDimension !== undefined) {
    const inverseColorScale = colorDimension.greater_is_better === false;
    data.data[0].line = {
      ...data.data[0].line,
      color: colorDimension.values,
      colorbar: { ...data.data[0].line.colorbar, title: { text: colorDimension.label } },
      reversescale: inverseColorScale,
    };
  }
  return data;
});

async function refreshExperimentGroups() {
  isLoadingGroups.value = true;
  const results = await api.listEstimatorReportGroups(tenantName.value, projectName.value);
  if (results !== null) {
    estimatorReportsGroups.value = results;
    isLoadingGroups.value = false;
    if (estimatorReportsGroups.value.length > 0) {
      // select first report group
      if (groupsAsTree.value.length > 0) {
        const first = groupsAsTree.value[0];
        if (first && first.children?.length) {
          const children = first.children;
          onGroupSelected([first.name, children[0].name]);
        }
      }
    } else {
      isLoadingPlot.value = false;
      isLoadingSearch.value = false;
    }
  } else {
    router.replace({ name: RouteNames.NotFound });
  }
}

async function _fetchSearchResults() {
  const [dataset, ..._] = selectedGroup.value;
  return await api.filterEstimatorReports(
    tenantName.value,
    projectName.value,
    dataset,
    searchParams
  );
}

async function refreshExperimentPlotResults() {
  isLoadingPlot.value = true;
  isLoadingSearch.value = true;
  plotReports.value = await _fetchSearchResults();
  isLoadingPlot.value = false;

  colorByDimensionIndex.value = -1;
  await refreshExperimentSearchResults();
  await sortResults();
}

async function refreshExperimentSearchResults(triggerPlotUpdate: boolean = true) {
  isLoadingSearch.value = true;
  searchReports.value = await _fetchSearchResults();
  isLoadingSearch.value = false;

  if (triggerPlotUpdate) {
    updatePlot();
  }
}

function updatePlot() {
  const newDataSources = new Set<string>();
  for (const report of plotReports.value) {
    for (const metric of report.metrics) {
      if (metric.data_source !== null) {
        newDataSources.add(metric.data_source);
      }
    }
  }

  // build dimension map
  const source = isTestDatasource.value ? "test" : "train";
  const dimensionsMap = new Map<string, PlotDimesion>();
  for (const report of plotReports.value) {
    for (const metric of report.metrics) {
      if (
        metric.position !== null &&
        (metric.data_source === source || metric.data_source === null)
      ) {
        if (dimensionsMap.has(metric.name)) {
          const d = dimensionsMap.get(metric.name)!;
          d.values.push(metric.value);
          d.range[0] = Math.min(d.range[0], metric.value);
          d.range[1] = Math.max(d.range[1], metric.value);
        } else {
          dimensionsMap.set(metric.name, {
            range: [metric.value, metric.value],
            label: metric.verbose_name.length > 0 ? metric.verbose_name : metric.name,
            name: metric.name,
            values: [metric.value],
            position: metric.position,
            greater_is_better: metric.greater_is_better,
          });
        }
        if (!dimensionsNameByLabel.has(metric.verbose_name)) {
          dimensionsNameByLabel.set(metric.verbose_name, metric.name);
        }
      }
    }
  }
  // check that the dimension map contains only metrics that are available in all results
  const allMetrics = plotReports.value.flatMap((r) =>
    r.metrics.filter((m) => m.position !== null).map((m) => m.name)
  );
  const commonMetricsNames = [...plotReports.value].reduce(
    (accum, report) =>
      intersect(
        accum,
        report.metrics.map((m) => m.name)
      ),
    allMetrics
  );

  dimensions.value = [...dimensionsMap.values()]
    .filter((d) => commonMetricsNames.includes(d.name))
    .sort((a, b) => a.position - b.position);
  if (colorByDimensionIndex.value === -1) {
    colorByDimensionIndex.value = dimensions.value.length - 1;
  }
}

function updateSearchQuery(dimensions?: { constraintrange: Array<any>; label: string }[]) {
  searchParams = new URLSearchParams();

  const [_, ml_task, estimator] = selectedGroup.value;
  if (ml_task) {
    searchParams.append("ml_task", ml_task);
  }
  if (estimator) {
    searchParams.append("estimator_class_name", estimator);
  }
  searchParams.append("data_source", isTestDatasource.value ? "test" : "train");
  if (dimensions) {
    dimensions.forEach(({ constraintrange, label }) => {
      // if the user has selected one range
      // plotly sends an array that contains 2 values ([1.6893174913194444, 2.06263274444239])
      // else plotly sends and array of array...
      // ([[0.7397982605502139, 1.0481891218256905],
      // [1.6893174913194444, 2.06263274444239]])
      let ranges = constraintrange;
      if (!Array.isArray(constraintrange[0])) {
        ranges = [constraintrange];
      }
      ranges.forEach((r) => {
        const [start, end] = r;
        const metric = dimensionsNameByLabel.get(label);
        searchParams.append(`metrics__${metric}__gte`, start);
        searchParams.append(`metrics__${metric}__lte`, end);
      });
    });
  }
}

async function onGroupSelected(path: string[]) {
  selectedGroup.value = path;
  colorByDimensionIndex.value = -1;
  updateSearchQuery();
  await refreshExperimentPlotResults();
}

async function onPlotDataChange(data: any) {
  const dimensions = data[0]["dimensions"];
  const constrainedDimensions = dimensions.filter((d: any) => "constraintrange" in d);
  updateSearchQuery(constrainedDimensions);
  await refreshExperimentSearchResults(false);
}

async function sortResults() {
  const colorBy = dimensions.value[colorByDimensionIndex.value];
  const source = isTestDatasource.value ? "test" : "train";

  const getCriterion = (r: EstimatorReportSearchResultDto) =>
    r.metrics.find((m) => m.name === colorBy.name && m.data_source === source)?.value ?? 0;

  const results = [...searchReports.value].sort((a, b) => getCriterion(a) - getCriterion(b));
  if (colorBy.greater_is_better) {
    results.reverse();
  }
  searchReports.value = results;

  const colors: string[] = [];
  for (const report of searchReports.value) {
    const value = getCriterion(report);
    const range = dimensions.value.find((d) => d.name == colorBy.name)?.range;
    if (range) {
      let ratio = 0.5;
      if (range[0] != range[1]) {
        if (colorBy.greater_is_better) {
          ratio = remap(value, range[0], range[1], 0, 1);
        } else {
          ratio = remap(value, range[0], range[1], 1, 0);
        }
      }
      const color = await getColorInGradient(ratio, gradient);
      colors.push(color);
    }
  }
  searchReportsColors.value = colors;
}

onMounted(async () => {
  await refreshExperimentGroups();
});

watch(colorByDimensionIndex, async () => {
  updatePlot();
  await sortResults();
});

watch(isTestDatasource, async () => {
  updateSearchQuery();
  await refreshExperimentSearchResults();
  await sortResults();
});
</script>

<template>
  <div class="report-search">
    <GridLayoutWithSidebar>
      <template #sidebar>
        <Transition name="fade" mode="out-in">
          <div v-if="isLoadingGroups" class="loading">
            <SkoreSpinner />
          </div>
          <div class="tree" v-else-if="groupsAsTree.length > 0">
            <TreeAccordion :nodes="groupsAsTree" selectable @itemSelected="onGroupSelected" />
          </div>
          <div v-else class="empty">
            <div class="title label-s">No items</div>
            <div class="subtitle paragraph-s">
              We didn’t find any project structure, try to send it from skore lib.
            </div>
          </div>
        </Transition>
      </template>
      <template #topTitle>
        <div class="title label-s" v-if="estimatorReportsGroups.length > 0">
          <em>Model Finder</em> - drag up and down to brush on axis
        </div>
        <div class="plot-settings" v-if="!isLoadingPlot && estimatorReportsGroups.length > 0">
          <div>
            Color by:
            <DropdownButton
              :label="dimensions[colorByDimensionIndex]?.label"
              icon-right="icon-chevron-down"
            >
              <DropdownButtonItem
                v-for="(dimension, index) in dimensions"
                :key="dimension.label"
                :label="dimension.label"
                @click="colorByDimensionIndex = index"
              />
            </DropdownButton>
            <div class="legend">
              <div v-if="dimensions[colorByDimensionIndex]?.greater_is_better">
                <i class="icon icon-ascending-arrow" />Higher is better
              </div>
              <div v-else><i class="icon icon-descending-arrow" />Lower is better</div>
            </div>
          </div>
          <div>
            <DataSourceToggle v-model:is-test-datasource="isTestDatasource" />
          </div>
        </div>
      </template>
      <template #top>
        <Transition name="fade" mode="out-in">
          <div v-if="isLoadingPlot" class="loading">
            <SkoreSpinner />
          </div>
          <div v-else-if="estimatorReportsGroups.length > 0" class="plot-container">
            <div class="plot-scaler">
              <PlotlyWidget
                :spec="plotData"
                :display-mode-bar="false"
                @data-change="onPlotDataChange"
              />
            </div>
          </div>
          <div v-else class="empty">
            <div class="title label-s">No items</div>
            <div class="subtitle paragraph-s">
              No item selected. Try to first select an item in the estimator group sidebar.
            </div>
          </div>
        </Transition>
      </template>
      <template #bottom>
        <Transition name="fade" mode="out-in">
          <div v-if="isLoadingSearch" class="loading">
            <SkoreSpinner />
          </div>
          <div class="results" v-else-if="estimatorReportsGroups.length > 0">
            <div class="results-header">
              <div class="results-count label-m">
                {{ searchReports.length }} result{{ searchReports.length > 1 ? "s" : "" }}
              </div>
              <RouterLink
                class="skore-button primary"
                :to="{
                  name: RouteNames.ComparisonBuilder,
                  params: { tenantName, projectName },
                  query: { reportId: searchReports.map((e) => e.id) },
                }"
              >
                <i class="icon left icon-arrow-loop" />
                <span class="label">Compare</span>
              </RouterLink>
            </div>
            <div class="results-cards">
              <ProjectDetailViewExperimentsSearchResult
                v-for="(report, index) in searchReports"
                :key="report.id"
                :report="report"
                :style="{ '--color': searchReportsColors[index] }"
              />
            </div>
          </div>
          <div v-else class="empty">
            <div class="title label-s">No experiments filtered</div>
            <div class="subtitle paragraph-s">No experiment found.</div>
          </div>
        </Transition>
      </template>
    </GridLayoutWithSidebar>
  </div>
</template>

<style scoped>
.loading {
  padding: var(--spacing-20);
  text-align: center;
}

.report-search {
  height: 100%;

  .grid-layout-with-sidebar {
    height: 100%;
  }

  .tree {
    padding: var(--spacing-8);
  }

  .title {
    padding: var(--spacing-8);
    color: var(--color-text-secondary);

    em {
      color: var(--color-text-primary);
      font-style: normal;
    }
  }

  .plot-settings {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0 var(--spacing-8) var(--spacing-8) var(--spacing-8);

    > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: var(--spacing-6);
    }

    .legend {
      i {
        padding-right: var(--spacing-6);
        vertical-align: middle;

        &.icon-ascending-arrow {
          color: var(--color-positive);
        }

        &.icon-descending-arrow {
          color: var(--color-negative);
        }
      }
    }
  }

  .plot-container {
    height: 100%;

    .plot-scaler {
      /* help readability by scaling up the plot */
      width: 75%;
      height: 75%;
      transform: scale(1.33);
      transform-origin: top left;
    }
  }

  .results {
    --nb-columns: 1;

    container-name: results;
    container-type: inline-size;

    .results-header {
      position: sticky;
      top: 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      padding: var(--spacing-8);
      background-color: var(--color-background-primary);

      .results-count {
        color: var(--color-text-branding);
      }
    }

    .results-cards {
      display: grid;
      padding: var(--spacing-8);
      gap: var(--spacing-8);
      grid-template-columns: repeat(var(--nb-columns), 1fr);

      :deep(.header .icon) {
        color: var(--color);
      }
    }
  }

  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;

    .subtitle {
      color: var(--color-text-secondary);
    }
  }

  .sidebar .empty {
    height: calc(var(--content-height) - var(--spacing-20));
  }

  .top .empty {
    height: 100%;
  }

  .bottom .empty {
    margin-top: var(--spacing-80);
  }

  .top .empty,
  .bottom .empty {
    > div {
      max-width: 40%;
    }
  }
}

@media screen and (width >= 1025px) {
  .report-search .results {
    --nb-columns: 2;
  }
}

@supports (animation-timeline: scroll()) {
  .results-header {
    animation: scroll-shadow linear both;
    animation-range: 0 calc(32px + (var(--spacing-8) * 2));
    animation-timeline: scroll();
  }

  @keyframes scroll-shadow {
    from {
      box-shadow: none;
    }

    to {
      box-shadow:
        0 4px 8px -2px rgb(16 24 40 / 10%),
        0 2px 4px -2px rgb(16 24 40 / 6%);
    }
  }
}
</style>
